/**
 * Structured exception thrown by {@link parseEmail} (and any future helpers
 * that share the parse contract) when input cannot be parsed at all.
 *
 * The parser itself is intentionally permissive — malformed bytes,
 * unknown charsets, missing boundaries are all handled by silent
 * fallbacks documented at the call site. `EmailParseError` is reserved
 * for the small set of *genuine* failures: an empty source, a source
 * with no header content, and similar situations where there is nothing
 * meaningful to return.
 *
 * Callers (today: `useEmail.parse` / `useEmail.parseAndReplace`) catch
 * this with the existing top-level `try/catch` and surface
 * `error.message` to the user. The structured fields (`offset`,
 * `header`, `reason`) exist so the future "show parse error context"
 * UI can highlight where in the source the parser gave up without
 * having to re-parse the message.
 */
export interface EmailParseErrorFields {
  /** Byte offset in the normalized source where parsing failed, if known. */
  offset?: number;
  /** Header name (lowercase) where parsing failed, if applicable. */
  header?: string;
  /** Short, stable identifier for the failure mode (e.g. `"empty-input"`). */
  reason: string;
}

/**
 * Thrown by `parseEmail` for failure modes that cannot be recovered with
 * a permissive fallback. Extends `Error` so existing
 * `err instanceof Error` checks continue to work; adds three structured
 * fields for callers that want richer diagnostics.
 */
export class EmailParseError extends Error {
  /** Byte offset in the normalized source where parsing failed, if known. */
  readonly offset?: number;
  /** Header name (lowercase) where parsing failed, if applicable. */
  readonly header?: string;
  /** Short, stable identifier for the failure mode. */
  readonly reason: string;

  constructor(message: string, fields: EmailParseErrorFields) {
    super(message);
    this.name = "EmailParseError";
    this.reason = fields.reason;
    if (fields.offset !== undefined) this.offset = fields.offset;
    if (fields.header !== undefined) this.header = fields.header;
    // Preserve prototype chain across transpile / minify boundaries so
    // `instanceof EmailParseError` works in older bundlers too.
    Object.setPrototypeOf(this, EmailParseError.prototype);
  }
}

/**
 * Heuristic that returns true when a pasted blob looks like a saved HTML page
 * rather than raw RFC 5322 message source. Users commonly paste Gmail /
 * Outlook's "View original" preview HTML, the entire rendered inbox page, or
 * a "save as web page" archive — none of which carry a header block the MIME
 * parser can walk. We sniff the first ~512 chars (after trimming leading
 * whitespace + an optional BOM) for the canonical HTML markers:
 *
 * - `<!doctype html` (case-insensitive) - `<html` (case-insensitive) -
 * `<head` or `<body` early in the source
 *
 * The check is intentionally narrow: an.eml whose body contains an HTML part
 * starts with `Return-Path:` / `Received:` / `From:` etc. — the header block
 * precedes any inline HTML, so the markers above never land in the sniffed
 * prefix.
 *
 * Surfaced as a one-line hint inside the PreParseHero error block (see
 * `errorHtmlHint` in `SourcePanelExtrasCopy`).
 */
export function looksLikeHtmlSource(source: string): boolean {
  if (typeof source !== "string" || source.length === 0) return false;
  // Strip a leading BOM + whitespace so the sniff doesn't miss a doc
  // whose first byte is U+FEFF before `<!doctype html>`. The codepoint is
  // spelled as an escape because eslint's no-irregular-whitespace rule
  // forbids the raw codepoint in source text.
  const head = source
    .replace(/^\uFEFF/, "")
    .trimStart()
    .slice(0, 512)
    .toLowerCase();
  if (head.length === 0) return false;
  // `<!doctype html` or `<html` at the very start is unambiguous —
  // RFC 5322 sources never start with a `<` token.
  if (/^<!doctype\s+html\b/.test(head) || /^<html[\s>]/.test(head)) {
    return true;
  }
  // Some "save as web page" exports prepend an XML / declaration block; allow
  // `<head` or `<body` within the first 512 chars as a secondary signal, but
  // only when no header-looking line precedes them (a real.eml whose body
  // contains `<head` would always have `From:` / `Received:` / `Subject:`
  // etc. before it).
  const hasHtmlTag = /<(head|body)\b/.test(head);
  if (!hasHtmlTag) return false;
  const looksLikeHeaders = /^[a-z][a-z0-9-]*\s*:/m.test(head);
  return !looksLikeHeaders;
}
